import {  Heart, Sparkles, Zap } from 'lucide-react';

const Flavors = () => {
    
    // --- 🍦 PREMIUM DATA (Valid Images) ---
    const signatures = [
        {
            id: 1,
            name: "Blueberry Cheesecake",
            desc: "Creamy cheesecake base swirled with homemade blueberry compote.",
            price: "Tk.450",
            // Image: Blueberry Scoop
            img: "https://images.unsplash.com/photo-1497034825429-c343d7c6a68f?q=80&w=800&auto=format&fit=crop",
            tag: "Bestseller",
            tagColor: "bg-purple-100 text-purple-600"
        },
        {
            id: 2,
            name: "Honey Roasted Fig",
            desc: "Caramelized figs blended with organic honey and a hint of thyme.",
            price: "Tk.500",
            // Image: Fancy Plated Ice Cream
            img: "https://images.unsplash.com/photo-1563805042-7684c019e1cb?q=80&w=800&auto=format&fit=crop",
            tag: "New Arrival",
            tagColor: "bg-orange-100 text-orange-600"
        },
        {
            id: 3,
            name: "Artistic Scoop in Bowl",
            desc: "Ceremonial grade matcha from Japan. Earthy, creamy, and distinct.",
            price: "Tk.480",
            // Image: Green Scoop
            img: "https://images.unsplash.com/photo-1579954115563-e72bf1381629?auto=format&fit=crop&q=80&w=800",
            tag: "Staff Pick",
            tagColor: "bg-green-100 text-green-600"
        }
    ];

    const classics = [
        { id: 4, name: "Belgian Chocolate", color: "bg-[#3e2723]", img: "https://images.unsplash.com/photo-1579954115545-a95591f28bfc?q=80&w=500&auto=format&fit=crop" },
        { id: 5, name: "Madagascar Vanilla", color: "bg-[#fff9c4]", img: "https://images.unsplash.com/photo-1570197788417-0e82375c9371?q=80&w=500&auto=format&fit=crop" },
        { id: 6, name: "Fresh Strawberry", color: "bg-[#f8bbd0]", img: "https://images.unsplash.com/photo-1488900128323-21503983a07e?q=80&w=500&auto=format&fit=crop" },
        { id: 7, name: "Mint Chip", color: "bg-[#b2dfdb]", img: "https://images.unsplash.com/photo-1505575967455-40e256f73376?q=80&w=500&auto=format&fit=crop" },
    ];

    return (
        // 1. Added pt-32 to push content down below floating menu
        <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20 overflow-x-hidden">
            
            {/* --- Background Decor (Subtle) --- */}
            <div className="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden z-0">
                <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-pink-200/30 rounded-full blur-[100px]"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-yellow-200/30 rounded-full blur-[100px]"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">

                {/* --- HEADER --- */}
                <div className="mb-12">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={18} className="text-yellow-500 fill-current animate-pulse" />
                        <span className="text-xs font-bold tracking-[0.2em] text-gray-400 uppercase">Artisan Collection</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight">
                        Flavors that <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">Melt Your Heart</span>
                    </h1>
                </div>

                {/* --- 🌟 SECTION 1: SIGNATURE SCOOPS (Large Cards) --- */}
                <div className="mb-16">
                    <div className="flex justify-between items-end mb-6">
                        <h2 className="text-xl font-bold text-gray-800">Chef's Specials</h2>
                        <span className="text-sm font-semibold text-pink-500">View All</span>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {signatures.map((item) => (
                            // App-like Card: Rounded-3xl, soft shadow
                            <div key={item.id} className="bg-white rounded-[2rem] p-4 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.15)] transition-all duration-300 transform hover:-translate-y-1 group">
                                
                                {/* Image Container */}
                                <div className="relative h-64 w-full rounded-[1.5rem] overflow-hidden mb-4">
                                    <img 
                                        src={item.img} 
                                        alt={item.name} 
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                                    />
                                    
                                    {/* Glass Badge */}
                                    <div className="absolute top-3 left-3">
                                        <span className={`px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md bg-white/90 shadow-sm ${item.tagColor}`}>
                                            {item.tag}
                                        </span>
                                    </div>

                                    {/* Like Button */}
                                    <button className="absolute top-3 right-3 p-2 rounded-full bg-black/20 backdrop-blur-md text-white hover:bg-red-500 hover:text-white transition-colors">
                                        <Heart size={18} />
                                    </button>
                                </div>

                                {/* Info */}
                                <div className="px-2 pb-2">
                                    <div className="flex justify-between items-start mb-1">
                                        <h3 className="text-lg font-extrabold text-gray-900">{item.name}</h3>
                                        <span className="font-bold text-gray-900">{item.price}</span>
                                    </div>
                                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* --- 🍦 SECTION 2: THE CLASSICS (Horizontal Scroll / Grid) --- */}
                <div>
                    <div className="flex items-center gap-2 mb-6">
                        <Zap size={20} className="text-yellow-500 fill-current" />
                        <h2 className="text-xl font-bold text-gray-800">Always Available</h2>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {classics.map((item) => (
                            <div key={item.id} className="relative group cursor-pointer">
                                {/* Image Card */}
                                <div className="aspect-[4/5] rounded-[2rem] overflow-hidden relative shadow-md">
                                    <img src={item.img} alt={item.name} className="w-full h-full object-cover" />
                                    {/* Overlay Gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
                                    
                                    {/* Text overlay */}
                                    <div className="absolute bottom-4 left-4 right-4 text-white">
                                        <h3 className="font-bold text-lg leading-tight">{item.name}</h3>
                                        <span className="text-xs font-medium text-gray-200">Classic</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Flavors;